import { useRef } from "react"
import { cx } from "@emotion/css"
import { getPrefixedClassNames, styles } from "./styles"
import { ColumnDefinition, Area } from "./types"
import { DEFAULT_MIN_COLUMN_WIDTH, DEFAULT_MAX_COLUMN_WIDTH } from "./constants"
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown"
import { toCamelCase, getValueInDbFormat } from "utils/strings"
import { ColumnResizer } from "./ColumnResizer"
import { useTableContext } from "./context"
import { ISortingByColumn } from "models/spots"

const sort_order = ["descending", "ascending"] as const

export const getSortOrder = (
  boolOrder: boolean,
): "descending" | "ascending" => {
  return sort_order[Number(boolOrder)]
}

type HeaderCellProps = {
  column: ColumnDefinition
  area: Area
  sortingByColumn?: ISortingByColumn
}

export function HeaderCell({ column, area, sortingByColumn }: HeaderCellProps) {
  const {
    id: columnId,
    width,
    align = "left",
    dataKey,
    Header,
    resizable = true,
    maxWidth,
    minWidth,
    sortable = true,
  } = column

  const ref = useRef<HTMLDivElement | null>(null)

  const {
    columnManager: cm,
    onColumnResize: onColumnResizeProp,
    activeSortColumn,
    selectedSortOrder,
    onSortColumnChange,
  } = useTableContext()

  const handleResizerDone = (w: number) => {
    cm.onColumnResize(w, column)
    onColumnResizeProp && onColumnResizeProp(w, column)
  }

  const sortOrder = getSortOrder(selectedSortOrder)
  const sortProps = sortable
    ? ({
        role: "button",
        "aria-sort": sortOrder,
        onClick: () => {
          const sortingState = sortingByColumn?.sortingState
          const setSortingState = sortingByColumn?.setSortingState
          const columnHeader = toCamelCase(
            column.Header as string,
          ).toLowerCase()
          const columnHeaderInDbFormat = getValueInDbFormat(columnHeader)
          const newSortingOrder =
            sortingState?.[columnHeaderInDbFormat] === "ascending"
              ? "descending"
              : "ascending"
          if (setSortingState) {
            setSortingState({
              ...sortingState,
              sortedBy: columnHeaderInDbFormat,
              [columnHeaderInDbFormat]: newSortingOrder,
            })
          }
          onSortColumnChange(column.id)
        },
      } as const)
    : {}

  return (
    <div
      ref={ref}
      data-grid-header-cell
      data-column-id={columnId}
      className={cx(
        styles.cell,
        styles.headerCell,
        styles.aligns[align],
        { [styles.sortable]: column.sortable },
        getPrefixedClassNames("cell", "header-cell", `header-cell--${area}`),
      )}
      style={{ width }}
      {...sortProps}
    >
      {Header === null || typeof Header === "string" ? (
        <span className={styles.cellText}>{Header}</span>
      ) : (
        <Header id={columnId} dataKey={dataKey} align={align} />
      )}
      {column.sortable ? (
        <ArrowDropDownIcon
          className={cx(styles.descending, {
            [styles.ascending]: sortingByColumn
              ? localStorage.getItem("sortingOrder") === "ascending" &&
                localStorage.getItem("sortingColumn") ===
                  getValueInDbFormat(
                    toCamelCase(String(column.Header)).toLowerCase(),
                  )
              : sortOrder === "ascending" && activeSortColumn === columnId,
            [styles.activeColumn]: activeSortColumn === columnId,
          })}
        />
      ) : null}
      {resizable ? (
        <ColumnResizer
          cellRef={ref}
          minWidth={minWidth ?? DEFAULT_MIN_COLUMN_WIDTH}
          maxWidth={maxWidth ?? DEFAULT_MAX_COLUMN_WIDTH}
          onDone={handleResizerDone}
        />
      ) : null}
    </div>
  )
}
